import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingCart, User, LogOut, Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import { useAuthStore } from "../store/authStore";
import { useCartStore } from "../store/cartStore";
import { BottomNav } from "./BottomNav";
import TransportTicker from "./TransportTicker";
import { logActivity } from "../lib/telemetry";

// Identité officielle La BASE
const LOGO = "/brand/labase-logo.png";
const PIN_247 = "/brand/pin-247.png";
const GREEN = "#075A43";
const GREEN_DARK = "#004A36";
const ORANGE = "#FF5A1F";

const NAV = [
  { to: "/", label: "Accueil" },
  { to: "/restaurant-boissons", label: "Boutique" },
  { to: "/personal-shopper", label: "Personal Shopper" },
  { to: "/tyluss", label: "Tyluss" },
  { to: "/commandes", label: "Mes commandes" },
];

export const Layout = ({ children }) => {
  const nav = useNavigate();
  const loc = useLocation();
  const { user, token, logout } = useAuthStore();
  const count = useCartStore((s) => s.getItemCount());
  const [open, setOpen] = useState(false);

  const isActive = (to) => (to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(to));

  useEffect(() => { logActivity("page_view", { path: loc.pathname }); }, [loc.pathname]);

  // Bandeau Transport : visible pendant la navigation, SAUF accueil et checkout.
  const hideTicker = loc.pathname === "/accueil" || loc.pathname.startsWith("/checkout") || loc.pathname.includes("/checkout");

  return (
    <div className="min-h-screen flex flex-col pb-[88px] md:pb-0" style={{ background: "#FCFCFA" }}>
      <header className="sticky top-0 z-50" style={{ background: "#FFFFFF", borderBottom: "1px solid #E9ECEA" }}>
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="brand-logo">
            <img src={LOGO} alt="La BASE" style={{ height: 40, width: "auto" }} />
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.to === "/" ? "home" : n.to.slice(1)}`}
                className="px-3 py-2 rounded-lg text-sm font-semibold transition-colors"
                style={{ color: isActive(n.to) ? "#fff" : GREEN_DARK, background: isActive(n.to) ? ORANGE : "transparent" }}>
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link to="/panier" data-testid="nav-cart" className="relative p-2 rounded-lg" style={{ color: GREEN_DARK }}>
              <ShoppingCart size={22} />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 text-[11px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
                  style={{ background: ORANGE, color: "#fff" }} data-testid="cart-count">{count}</span>
              )}
            </Link>
            {token ? (
              <div className="hidden md:flex items-center gap-2">
                <Link to="/profil" className="p-2 rounded-lg flex items-center gap-2" style={{ color: GREEN_DARK }} data-testid="nav-profile">
                  <User size={20} />
                  <span className="text-sm font-medium max-w-[120px] truncate">{user?.name}</span>
                </Link>
                <button onClick={() => { logout(); nav("/login"); }} className="p-2 rounded-lg" style={{ color: ORANGE }} data-testid="logout-btn" title="Se déconnecter">
                  <LogOut size={20} />
                </button>
              </div>
            ) : (
              <Link to="/login" className="hidden md:block px-4 py-2 rounded-lg text-sm font-bold" style={{ background: ORANGE, color: "#fff" }} data-testid="nav-login">
                Se connecter
              </Link>
            )}
            <img src={PIN_247} alt="24/7" style={{ height: 44, width: "auto" }} data-testid="brand-pin-247" />
            <button className="md:hidden p-2 rounded-lg" style={{ color: GREEN_DARK }} onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-btn">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="md:hidden px-4 pb-3 flex flex-col gap-1" style={{ background: "#FFFFFF", borderTop: "1px solid #E9ECEA" }}>
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
                className="px-3 py-2 rounded-lg text-sm font-semibold"
                style={{ color: isActive(n.to) ? "#fff" : GREEN_DARK, background: isActive(n.to) ? ORANGE : "transparent" }}>
                {n.label}
              </Link>
            ))}
            {token ? (
              <>
                <Link to="/profil" onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm font-semibold" style={{ color: GREEN_DARK }}>Mon profil</Link>
                <button onClick={() => { logout(); setOpen(false); nav("/login"); }} className="px-3 py-2 rounded-lg text-sm font-semibold text-left" style={{ color: ORANGE }}>Se déconnecter</button>
              </>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm font-bold" style={{ background: ORANGE, color: "#fff" }}>Se connecter</Link>
            )}
          </div>
        )}
      </header>

      {!hideTicker && <TransportTicker />}

      <main className="flex-1 w-full">{children}</main>

      <footer className="mt-8" style={{ background: GREEN_DARK, color: "#c7d6d0" }}>
        <div className="max-w-6xl mx-auto px-4 py-8 text-sm flex flex-col md:flex-row items-center justify-between gap-3">
          <img src={LOGO} alt="La BASE" style={{ height: 30, background: "#fff", borderRadius: 6, padding: 3 }} />
          <span>La 1ère plateforme 100% locale — Broye &amp; Vully</span>
          <span>© {new Date().getFullYear()} LA BASE</span>
        </div>
      </footer>

      <BottomNav />
    </div>
  );
};
